import {
  getPriceInCentimes,
  locationOfCourse,
  PaginatedAnnounceSearchResult,
  SearchAnnouncePaginatedQueryDto,
  SortCriteria,
} from '@les-sherpas/sherpas-toolbox';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { MAX_PRICE_SEARCH_VALUE } from '@/components/Search/components/Filters/Filters.stub';
import { MAX_VALUE } from '@/components/Search/components/Filters/PriceFilter/PriceFilter';
import {
  indifferentLocationOfCourse,
  SearchFiltersSortDebounceType,
} from '@/components/Search/types';
import {
  ASCENDING_PRICE,
  getSearchResult,
  sortCriteria,
} from '@/components/Search/utils';

export const useGetPaginatedAnnouncesWithCriteria = ({
  filters: { subject, address, sidePanelFilters, minPrice, maxPrice },
  sort: { criteria },
  onFetch = () => {},
}: SearchFiltersSortDebounceType & { onFetch: () => void }) => {
  const formattedMaxPrice =
    maxPrice >= MAX_VALUE ? MAX_PRICE_SEARCH_VALUE : maxPrice;
  const {
    data,
    isLoading,
    isError,
    isPreviousData,
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<PaginatedAnnounceSearchResult>({
    queryKey: [
      'search',
      {
        ...sidePanelFilters,
        minPrice,
        maxPrice: formattedMaxPrice,
        subject,
        address,
        sortCriteria: criteria,
      },
    ],
    queryFn: ({ pageParam }) => {
      const {
        courseLocation,
        availabilities,
        gender,
        educationalStages,
        onlyAvailableSherpas,
        onlyCertifiedSherpas,
        onlyAnswerFastSherpas,
      } = sidePanelFilters;
      const searchInput: SearchAnnouncePaginatedQueryDto = {
        filters: {
          onlyCertifiedSherpas,
          onlyAvailableSherpas,
          onlyAnswerFastSherpas,
          gender,
          educationalStages,
          availabilities,
          minPrice: getPriceInCentimes(minPrice),
          maxPrice: getPriceInCentimes(formattedMaxPrice),
          subject: subject || '',
          address,
          isFromSeo: false,
          alreadyFetchedIds: pageParam?.fetchedIds || [],
          courseLocations:
            courseLocation === indifferentLocationOfCourse
              ? [locationOfCourse.atHome, locationOfCourse.online]
              : [courseLocation],
        },
        pagination: {
          currentPage: pageParam?.page || 1,
          sortCriteria: (criteria === sortCriteria.indexOf(ASCENDING_PRICE)
            ? SortCriteria.Price
            : sortCriteria[criteria]) as SortCriteria,
          sortByDescending: !(
            criteria === sortCriteria.indexOf(ASCENDING_PRICE)
          ),
        },
      };
      onFetch();
      return getSearchResult(searchInput);
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.pagination.hasMoreResults) {
        return {
          fetchedIds: allPages
            .flatMap(({ data: { results } }) => results.map(({ id }) => id))
            .flat(),
          page: lastPage.pagination.currentPage + 1,
        };
      }
      return undefined;
    },
    cacheTime: 0,
  });

  return {
    announces: data?.pages?.flatMap(({ data: { results } }) => results) || [],
    hasMore: hasNextPage,
    totalCount: data?.pages?.[0]?.data?.totalCount || 0,
    currentPage:
      data?.pages?.[data?.pages?.length - 1 || 0]?.pagination.currentPage || 0,
    isFetching,
    isLoading,
    isPreviousData,
    isError,
    fetchNextPage,
  };
};

export const useGetHasActiveSubscription = ({
  enabled,
}: {
  enabled: boolean;
}) => {
  const { data: hasSubscription } = useQuery(
    ['student/has-active-subscription'],
    {
      enabled,
    }
  );
  return hasSubscription;
};
